import type { ReactNode } from "react";
import {
  equipmentDefinition,
  type EquipmentKey,
} from "@frontier/content/arsenal";
import {
  MODULES,
  moduleEnergyCost,
  WEAPONS,
} from "@frontier/content/definitions";
import {
  equipmentParameters,
  type EquipmentTier,
} from "@frontier/content/equipment-tiers";
import { SHIP_SYSTEMS } from "@frontier/content/ship-systems";
import type { Profile } from "@frontier/protocol/contracts";
import { EquipmentArt, equipmentBackdrop } from "./EquipmentArt";
import { equipmentCount, mountedCount } from "./equipment-selection";
import { passiveInfo } from "./flight-labels";
import { moduleDescriptions, weaponDescriptions } from "./ui-labels";

export function equipmentDisplayParameters(
  key: EquipmentKey,
  tier: EquipmentTier,
  hullId: Profile["hullId"],
) {
  const parameters = equipmentParameters(key, tier);
  return key.kind === "module"
    ? [
        ...parameters,
        {
          label: "Энергия активации",
          value: moduleEnergyCost(key.id, hullId),
          unit: "ЭН",
        },
        { label: "Перезарядка", value: MODULES[key.id].cooldown, unit: "с" },
      ]
    : key.kind === "weapon"
      ? [
          ...parameters,
          { label: "Дальность", value: WEAPONS[key.id].range, unit: "" },
          {
            label: "Интервал выстрелов",
            value: WEAPONS[key.id].cooldown,
            unit: "с",
          },
        ]
      : parameters;
}
export function equipmentValue(value: number, unit: string): string {
  return `${Number(value.toFixed(2)).toLocaleString("ru-RU")} ${unit}`.trim();
}
export function EquipmentPresentation({
  equipment,
  tier,
  profile,
  status,
  children,
}: {
  equipment: EquipmentKey;
  tier: EquipmentTier;
  profile: Profile;
  status: string;
  children?: ReactNode;
}) {
  const item = equipmentDefinition(equipment);
  const backdrop = equipmentBackdrop(equipment);
  const total = equipmentCount(profile.arsenal, equipment, tier);
  const mounted = mountedCount(profile, equipment, tier);
  const description =
    equipment.kind === "system"
      ? SHIP_SYSTEMS[equipment.id].description
      : equipment.kind === "weapon"
        ? weaponDescriptions[equipment.id]
        : equipment.kind === "module"
          ? moduleDescriptions[equipment.id]
          : passiveInfo(equipment.id).description;
  return (
    <>
      <header className="fitting-item-hero">
        <span className="fitting-item-art" aria-hidden="true">
          {backdrop && <img src={backdrop} alt="" />}
          <EquipmentArt equipment={equipment} />
        </span>
        <div className="fitting-item-heading">
          <span className="fitting-item-status">{status}</span>
          <h2>
            {item.name} <span>MK {"I".repeat(tier + 1)}</span>
          </h2>
          <small>
            Всего: {total} · свободно: {Math.max(0, total - mounted)} · на
            корабле: {mounted}
          </small>
        </div>
      </header>
      <p className="fitting-description">{description}</p>
      <dl className="fitting-parameters">
        {equipmentDisplayParameters(equipment, tier, profile.hullId).map(
          (parameter) => (
            <div key={parameter.label}>
              <dt>{parameter.label}</dt>
              <dd>{equipmentValue(parameter.value, parameter.unit)}</dd>
            </div>
          ),
        )}
      </dl>
      {children}
    </>
  );
}
